import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/constants/landing";

function NavLink({ link, close, mobile = false }) {
  const isRoute = link.href.startsWith("/");
  const cls = mobile
    ? "text-[15px] text-[var(--yg-text)] font-medium"
    : "text-[13.5px] text-[var(--yg-text-mute)] hover:text-white transition-colors";
  const testid = `${mobile ? "mobile-link" : "nav-link"}-${link.label.toLowerCase().replace(/\s+/g, "-")}`;
  if (isRoute) {
    return <Link to={link.href} onClick={close} className={cls} data-testid={testid}>{link.label}</Link>;
  }
  return <a href={link.href} onClick={close} className={cls} data-testid={testid}>{link.label}</a>;
}

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setOpen(false);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-[rgba(7,7,13,0.72)] border-b border-[var(--yg-border)]" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1240px] mx-auto px-5 sm:px-8 h-[64px] flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2 group" data-testid="nav-logo">
          <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-[10px] overflow-hidden shadow-[0_8px_24px_-8px_var(--yg-violet-glow)]">
            <img src="/logo.png" alt="Yugiq logo" className="w-full h-full object-cover" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="text-[15px] font-extrabold tracking-[-0.02em] text-white">Yugiq</span>
            <span className="text-[9px] font-medium tracking-[0.22em] text-[var(--yg-text-dim)] uppercase mt-[2px]">Technologies</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.href} link={l} />
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a href="#portfolio" className="yg-btn yg-btn-ghost" data-testid="nav-cta-work">Our Work</a>
          <a href="#audit" className="yg-btn yg-btn-primary" data-testid="nav-cta-audit">Free AI Audit</a>
        </div>

        <button
          className="md:hidden p-2 -mr-2 text-white"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden border-t border-[var(--yg-border)] bg-[rgba(7,7,13,0.98)] backdrop-blur-xl" data-testid="mobile-menu">
          <div className="px-5 py-6 flex flex-col gap-5">
            {NAV_LINKS.map((l) => (
              <NavLink key={l.href} link={l} close={close} mobile />
            ))}
            <div className="pt-4 flex flex-col gap-3 border-t border-[var(--yg-border)]">
              <a href="#portfolio" onClick={close} className="yg-btn yg-btn-ghost w-full" data-testid="mobile-cta-work">Our Work</a>
              <a href="#audit" onClick={close} className="yg-btn yg-btn-primary w-full" data-testid="mobile-cta-audit">Free AI Audit</a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
